// Copyright 2021 Carnegie Mellon University. All Rights Reserved.
// Released under a MIT (SEI)-style license. See LICENSE.md in the project root for license information.

import {
  ChangeDetectorRef,
  Component,
  Inject,
  Input,
  LOCALE_ID,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { formatDate } from '@angular/common';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatTableDataSource } from '@angular/material/table';
import { MatSort } from '@angular/material/sort';
import {
  ComnAuthQuery,
  ComnSettingsService,
  CrucibleDialogService,
  Theme,
} from '@cmusei/crucible-common';
import { ActivatedRoute } from '@angular/router';
import { ClipboardService } from 'ngx-clipboard';
import {
  combineLatest,
  forkJoin,
  from,
  interval,
  Observable,
  of,
  Subject,
} from 'rxjs';
import {
  catchError,
  filter,
  map,
  shareReplay,
  skip,
  startWith,
  switchMap,
  take,
  takeUntil,
  tap,
  withLatestFrom,
} from 'rxjs/operators';
import { EventStatus, SystemPermission } from 'src/app/generated/alloy.api';
import { Event as AlloyEvent } from 'src/app/generated/alloy.api/model/event';
import { EventTemplate } from 'src/app/generated/alloy.api/model/eventTemplate';
import { EventTemplateDataService } from 'src/app/data/event-template/event-template-data.service';
import { EventDataService } from 'src/app/data/event/event-data.service';
import { ALLOY_CURRENT_EVENT_STATUS } from 'src/app/shared/models/enums';
import { SignalRService } from 'src/app/shared/signalr/signalr.service';
import { EventTemplateQuery } from 'src/app/data/event-template/event-template.query';
import { EventQuery } from 'src/app/data/event/event.query';
import { UserDataService } from 'src/app/data/user/user-data.service';
import { UserEventsQuery } from '../../../data/event/user-events.query';
import { CurrentUserQuery } from 'src/app/data/user/user.query';
import { CurrentUserState } from 'src/app/data/user/user.store';
import { TopbarView } from '../../shared/top-bar/topbar.models';
import { PermissionDataService } from 'src/app/data/permission/permission-data.service';

@Component({
    selector: 'app-event-template-info',
    templateUrl: './event-template-info.component.html',
    styleUrls: ['./event-template-info.component.scss'],
    standalone: false
})
export class EventTemplateInfoComponent implements OnInit, OnDestroy {
  @Input() eventTemplateId: string;
  @ViewChild('historySort', { static: false }) sort: MatSort;
  public ALLOY_CURRENT_EVENT_STATUS = ALLOY_CURRENT_EVENT_STATUS;
  public EventStatus = EventStatus;
  public TopbarView = TopbarView;
  public titleText: string;

  readonly ONE_HOUR = 1000 * 3600;

  public impsDataSource: MatTableDataSource<AlloyEvent>;
  public displayedColumns: string[] = [
    'username',
    'status',
    'dateCreated',
    'statusDate',
  ];
  public templateId$: Observable<string>;
  public eventTemplate$: Observable<EventTemplate>;
  public events$: Observable<AlloyEvent[]>;
  public currentEvent: AlloyEvent;
  public currentEvent$: Observable<AlloyEvent>;
  public userEvents$: Observable<AlloyEvent[]>;
  public userEvents: AlloyEvent[] = [];
  public currentUserId = '';
  public isLoading$: Observable<boolean>;
  public pollingIntervalMS: number;
  public remainingTime: string;
  public timeRunningLow: boolean;
  public redeploying: boolean;
  public theme$: Observable<Theme>;
  public inviteShown: boolean = false;
  public isOwner: boolean = false;
  public viewId: string;
  public expirationDate: Date;
  public isIFrame: boolean;
  public inviteLink: string;
  public permissions: SystemPermission[] = [];
  public canViewAdministration = false;
  private unsubscribe$: Subject<null> = new Subject<null>();

  constructor(
    private settingsService: ComnSettingsService,
    private crucibleDialog: CrucibleDialogService,
    public eventTemplateDataService: EventTemplateDataService,
    public eventDataService: EventDataService,
    private eventTemplateQuery: EventTemplateQuery,
    private eventQuery: EventQuery,
    private userDataService: UserDataService,
    private userEventsQuery: UserEventsQuery,
    private authQuery: ComnAuthQuery,
    private currentUserQuery: CurrentUserQuery,
    private route: ActivatedRoute,
    private signalRService: SignalRService,
    private clipboardService: ClipboardService,
    private changeDetector: ChangeDetectorRef,
    private permissionDataService: PermissionDataService,
    public snackBar: MatSnackBar,
    @Inject(LOCALE_ID) private locale: string
  ) {
    this.titleText = this.settingsService.settings.AppTopBarText;
    this.theme$ = this.authQuery.userTheme$;

    this.impsDataSource = new MatTableDataSource<AlloyEvent>(
      new Array<AlloyEvent>()
    );
    this.remainingTime = '';
    this.timeRunningLow = false;
    this.pollingIntervalMS = parseInt(
      this.settingsService.settings.PollingIntervalMS,
      10
    );
  }

  ngOnInit() {
    this.isIFrame = this.isIframe();

    // Load permissions
    forkJoin([
      this.permissionDataService.load(),
      this.permissionDataService.loadGroupPermissions(),
    ])
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(
        (x) => {
          this.permissions = this.permissionDataService.permissions;
          this.canViewAdministration =
            this.permissionDataService.canViewAdministration();
        }
      );

    this.route.params
      .pipe(
        map((params) => [params['id'] as string, params['viewId'] as string]),
        switchMap(([id, viewId]) => {
          if (viewId) {
            this.viewId = viewId;
            this.eventTemplateDataService.loadTemplate(id);
            this.eventDataService.loadEvents(id);
            return this.eventDataService.getViewEvents(viewId);
          } else if (id) {
            this.eventTemplateDataService.loadTemplate(id);
            return this.eventDataService.loadEvents(id);
          } else {
            return of([]);
          }
        }),
        takeUntil(this.unsubscribe$)
      )
      .subscribe();

    this.eventTemplate$ = this.eventTemplateQuery.selectLoading().pipe(
      filter((loading) => !loading),
      withLatestFrom(this.route.params.pipe(map((p) => p['id'] as string))),
      map(([loading, id]) => id),
      switchMap((id) => {
        return this.eventTemplateQuery.selectEntity(
          id ? id : this.eventTemplateId
        );
      }),
      shareReplay(),
      // share({
      //   connector: () => new ReplaySubject(),
      // })
      takeUntil(this.unsubscribe$)
    );

    this.events$ = this.eventTemplate$.pipe(
      switchMap((t) => {
        return this.eventQuery
          .selectByEventTemplateId(t.id)
          .pipe(map((events) => events));
      }),
      tap((events) => {
        this.impsDataSource.data = events;
        if (this.sort) {
          this.impsDataSource.sort = this.sort;
        }
      }),
      shareReplay(),
      // share({
      //   connector: () => new ReplaySubject(),
      // })
      takeUntil(this.unsubscribe$)
    );

    this.userDataService.setCurrentUser();
    this.currentEvent$ = this.eventQuery.selectAll().pipe(
      withLatestFrom(
        this.currentUserQuery.select(),
        this.route.params.pipe(map((p) => p['id'] as string))
      ),
      map(([events, user, id]) => {
        this.currentUserId = user ? user.id : '';
        if (events.length >= 1) {
          let currentEvent: AlloyEvent = null;

          if (this.viewId != null) {
            currentEvent = this.resolveCurrentEvent(
              events.filter((e) => e.viewId === this.viewId)
            );
          } else {
            currentEvent = this.resolveCurrentEvent(
              events.filter(
                (e) => e.userId === user.id && e.eventTemplateId === id
              )
            );
          }

          if (currentEvent != null) {
            this.isOwner = currentEvent.userId === user.id;

            this.signalRService.joinEvent(currentEvent.id);

            this.expirationDate = currentEvent.expirationDate;
            this.remainingTime = this.calculateRemainingTime(
              currentEvent.expirationDate
            );
            this.inviteLink = this.getInviteLink(currentEvent);
          }
          this.currentEvent = currentEvent;
          this.changeDetector.markForCheck();
          return currentEvent;
        } else {
          this.currentEvent = null;
          return null;
        }
      }),
      shareReplay(),
      // share({
      //   connector: () => new ReplaySubject(),
      // })
      takeUntil(this.unsubscribe$)
    );

    this.userEvents$ = this.userEventsQuery.selectLoading().pipe(
      filter((loading) => !loading),
      withLatestFrom(this.route.params.pipe(map((p) => p['id'] as string))),
      switchMap(([loading, id]) => {
        return this.userEventsQuery.userEventsByTemplateId$(
          id ? id : this.eventTemplateId
        );
      }),
      filter((events) => events.length >= 1),
      map((events) => events.filter((e) => this.isEventActive(e.status))),
      tap((events) => {
        events.forEach((e) => this.signalRService.joinEvent(e.id));
        this.userEvents = events.filter(
          (m) => m.createdBy !== this.currentUserId
        );
      }),
      shareReplay(),
      // share({
      //   connector: () => new ReplaySubject(),
      // })
      takeUntil(this.unsubscribe$)
    );

    combineLatest([
      this.currentEvent$.pipe(startWith(null)),
      this.userEvents$.pipe(startWith(null)),
    ])
      .pipe(
        skip(1),
        tap(([currentEvent, userEvents]) => {
          const count = userEvents ? userEvents.length : 0;
          const currentId = currentEvent ? currentEvent.id : 'null';
          if (
            this.viewId != null &&
            (userEvents == null || userEvents.length === 0) &&
            (currentEvent == null || !this.isEventActive(currentEvent.status))
          ) {
            window.top.location.href = window.location.href.split('/view/')[0];
          }
        }),
        takeUntil(this.unsubscribe$)
      )
      .subscribe();

    interval(10000)
      .pipe(
        tap(
          () =>
            (this.remainingTime = this.calculateRemainingTime(
              this.expirationDate
            ))
        ),
        takeUntil(this.unsubscribe$)
      )
      .subscribe();
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next(null);
    this.unsubscribe$.complete();
  }

  getInviteLink(event: AlloyEvent) {
    if (!event.shareCode) {
      return null;
    }

    let baseURI = document.baseURI;

    if (baseURI.endsWith('/')) {
      baseURI = baseURI.slice(0, baseURI.length - 1);
    }

    const link = new URL(`${baseURI}/enlist/${event.shareCode}`);
    return link.toString();
  }
  copyInviteLink(event: AlloyEvent) {
    this.clipboardService.copy(this.getInviteLink(event));
  }

  /**
   * What the copy button on a failure puts on the clipboard, and what its tooltip shows. The
   * card itself stays deliberately generic - the diagnosis is admin-only - so this line is the
   * whole of what a user can hand an administrator: the four identifiers needed to find the
   * failure in the admin event list. Same text on the card and on a history row, so a report
   * reads the same whichever one the user copied from.
   * <p>
   * The status date is formatted the same way the history table formats its own Status Date
   * column, so a pasted report matches what the user is looking at. pageEventTemplate is the
   * template this page is showing: it supplies the name, which Event does not carry, and its id
   * as a fallback, since eventTemplateId is nullable on Event.
   */
  failureReportText(
    event: AlloyEvent,
    pageEventTemplate?: EventTemplate
  ): string {
    const eventTemplateId =
      event?.eventTemplateId ?? pageEventTemplate?.id ?? 'unknown';
    // Only name the template when the page's template is the event's template. The /view route
    // resolves the current event by viewId, which is not guaranteed to be an event of the
    // template on screen, and a report naming the wrong template misdirects the reader.
    const eventTemplateName =
      pageEventTemplate?.id === eventTemplateId ? pageEventTemplate?.name : null;
    const eventTemplate = eventTemplateName
      ? `${eventTemplateName} (${eventTemplateId})`
      : eventTemplateId;
    const statusDate = event?.statusDate
      ? formatDate(event.statusDate, 'long', this.locale)
      : 'unknown';
    const userId = event?.userId ?? 'unknown';
    // The name is what a human reads in a ticket, the id is what an administrator queries on, so
    // the report carries both. username is nullable on Event: with no name there is nothing to
    // put in front of the id, and "unknown (<id>)" would only add noise.
    const user = event?.username ? `${event.username} (${userId})` : userId;

    return (
      `Event ${event?.id ?? 'unknown'} launched from event template ` +
      `${eventTemplate} has failed to launch for user ` +
      `${user} at ${statusDate}.`
    );
  }
  /**
   * Picks the one event this page is about. A Failed event counts, because a user whose
   * launch broke needs to be told so - the old filter dropped it, the Launch button
   * silently came back, and the failure was never reported. An event that is still going
   * wins over a failed one, since a user who launched again cares about the new attempt.
   * <p>
   * Only the newest attempt can report a failure, and only while nothing newer exists.
   * Failed is terminal and the event stays in the store, so reporting any failure found
   * would pin this page to the failure card for the life of the account - hiding the
   * Launch and Join controls the user needs next, long after a later attempt succeeded
   * and ended.
   */
  private resolveCurrentEvent(candidates: AlloyEvent[]): AlloyEvent {
    const active = candidates.find((e) => this.isEventActive(e.status));

    if (active) {
      return active;
    }

    const newest = [...candidates].sort(
      (a, b) => this.attemptedAt(b) - this.attemptedAt(a)
    )[0];

    return newest?.status === EventStatus.Failed ? newest : null;
  }

  /**
   * When an attempt was made, for ordering. dateCreated is when the launch was requested and
   * is set on every Event; launchDate is not - a launch that failed before the scenario
   * started never gets one - so it cannot order failures against successes.
   */
  private attemptedAt(event: AlloyEvent): number {
    return event.dateCreated ? new Date(event.dateCreated).getTime() : 0;
  }

  isEventActive(s: EventStatus) {
    switch (s) {
      case EventStatus.Creating:
      case EventStatus.Active:
      case EventStatus.Paused:
      case EventStatus.Planning:
      case EventStatus.Applying:
      case EventStatus.Ending: {
        return true;
      }
      default: {
        return false;
      }
    }
  }

  determineEventStatus(event?: AlloyEvent) {
    if (event) {
      switch (event.status) {
        case EventStatus.Active:
          return ALLOY_CURRENT_EVENT_STATUS.LAUNCHED;
        case EventStatus.Creating:
        case EventStatus.Planning:
        case EventStatus.Applying:
          return ALLOY_CURRENT_EVENT_STATUS.LAUNCHING;
        case EventStatus.Ending:
          return ALLOY_CURRENT_EVENT_STATUS.ENDING;
        case EventStatus.Ended:
          return ALLOY_CURRENT_EVENT_STATUS.ENDED;
        case EventStatus.Failed:
          return ALLOY_CURRENT_EVENT_STATUS.FAILED;
        default:
          return ALLOY_CURRENT_EVENT_STATUS.LAUNCH;
      }
    }
    return ALLOY_CURRENT_EVENT_STATUS.LAUNCH;
  }

  launchEvent(id: string) {
    this.eventDataService
      .launchEvent(id)
      .pipe(
        // Join the event's SignalR group first, then read the event back once. The API only
        // broadcasts EventUpdated to that group, so anything that happened between the
        // launch response and the join - which is where a fast failure lands - would
        // otherwise never reach us, and the page would sit on "Please wait!" forever.
        switchMap((event: AlloyEvent) =>
          from(this.signalRService.joinEvent(event.id)).pipe(
            // A hub that cannot be reached must not stop the read-back below. Losing live
            // updates costs the user a stale card they can refresh; skipping the read-back
            // leaves them on "Please wait!" with no way to find out what happened.
            catchError((err) => {
              console.log(err);
              return of(null);
            }),
            map(() => event.id)
          )
        ),
        switchMap((eventId: string) => this.eventDataService.getEvent(eventId)),
        tap((event: AlloyEvent) => this.eventDataService.stateUpdate(event)),
        catchError((err) => {
          console.log(err);
          return of(null);
        }),
        takeUntil(this.unsubscribe$)
      )
      .subscribe();
  }

  rejoinEvent(event: AlloyEvent) {
    window.top.location.href =
      this.settingsService.settings.PlayerUIAddress + '/view/' + event.viewId;
  }

  endEvent(event: AlloyEvent) {
    if (event) {
      this.crucibleDialog
        .confirm({
          title: 'End Event',
          message: 'Are you sure that you want to end this event?',
        })
        .afterClosed()
        .pipe(take(1))
        .subscribe((confirmed) => {
          if (confirmed) {
            this.eventDataService.endEvent(event.id);
          }
        });
    }
  }

  redeployEvent(event: AlloyEvent) {
    if (event) {
      this.crucibleDialog
        .confirm({
          title: 'Redeploy Event',
          message: 'Are you sure that you want to redeploy this event?',
        })
        .afterClosed()
        .pipe(take(1))
        .subscribe((confirmed) => {
          if (confirmed) {
            this.redeploying = true;
            this.eventDataService.redeployEvent(event.id);
          }
        });
    }
  }

  inviteEvent(event: AlloyEvent) {
    this.eventDataService
      .inviteEvent(event.id)
      .pipe(take(1))
      .subscribe(() => {
        this.inviteShown = true;
      });
  }

  isIframe(): boolean {
    if (window.location !== window.parent.location) {
      // The page is in an iframe
      return true;
    } else {
      // The page is not in an iframe
      return false;
    }
  }

  calculateRemainingTime(expirationDate: Date): string {
    let timeLeft = '';

    if (expirationDate != null) {
      const now = new Date();
      const exp = new Date(expirationDate);

      let diffInMs: number = exp.valueOf() - now.valueOf();
      if (diffInMs < 0) {
        diffInMs = 0; // Force to zero.  Do not display a negative time.
      }
      const modHrs = (diffInMs / this.ONE_HOUR) % 1;
      const diffInHrs: number = diffInMs / (1000 * 3600) - modHrs;
      const modMins = Math.floor(modHrs * 60);
      timeLeft =
        'Time Remaining:  ' +
        diffInHrs.toString() +
        ' hrs ' +
        modMins +
        ' mins';

      this.timeRunningLow = diffInMs < this.ONE_HOUR;
    }

    return timeLeft;
  }
}
